<template>
  <div class="ca-form">
    <v-expansion-panels v-model="openPanels" multiple>
      <v-expansion-panel
        v-for="section in displaySections"
        :key="section.id"
        :value="section.id"
      >
        <v-expansion-panel-title class="bg-primary text-white">
          <div class="d-flex align-center gap-2">
            <v-icon size="20" color="white">
              {{ getSectionIcon(section) }}
            </v-icon>
            <span class="font-weight-medium">{{ tl(section.title) }}</span>
            <v-chip
              v-if="sectionErrors[section.id]?.length"
              size="x-small"
              color="error"
              variant="flat"
            >
              {{ sectionErrors[section.id].length }} {{ tl('error(s)') }}
            </v-chip>
          </div>
        </v-expansion-panel-title>

        <v-expansion-panel-text>
          <SectionRenderer
            :ref="(el: any) => setSectionRef(section.id, el)"
            :section="section"
            :form-data="formData"
            :readonly="readOnly"
            :ref-data="refData"
            :resolve-template-fn="resolveTemplate"
            :resolve-group-fn="resolveGroup"
            :show-errors="showAddressErrors"
            density="compact"
            @update:field="handleFieldUpdate"
            @update:address="handleAddressUpdate"
            @field-action="handleFieldAction"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import SectionRenderer from '../shared/renderers/SectionRenderer.vue';
import { useFormConfig } from './composables/useFormConfig';
import type { SectionDef, RefDataItem } from './composables/useFormConfig';

// Translation
const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage, { silent: true });
const tl = (text: string) => {
  if (!text) return '';
  const translated = t(`startTask.${text}`);
  return (translated && translated !== `startTask.${text}`) ? translated : text;
};

// Props
interface Props {
  taskConfig?: any;
  taskId?: string;
  entityCd?: string;
  loading?: boolean;
  readOnly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  taskId: undefined,
  entityCd: '',
  loading: false,
  readOnly: false,
});

// Emits
const emit = defineEmits<{
  'update:formData': [data: any];
  // companyNumber/companyName append-inner 아이콘 클릭 → 부모가 좌측 챗봇에
  //   entity selector 위젯을 띄우고, 선택된 entity 를 selectedEntity prop / applyEntity 로 주입.
  'entity-pick-request': [];
  // firmName ✨ 아이콘 클릭 → 부모가 챗봇에 officer-list 위젯(default SECRETARY) 띄우고
  //   선택된 officer 를 selectedOfficer prop / applyOfficer 로 주입.
  'firm-pick-request': [];
}>();

// Form config
const { sections: allSections, refData: baseRefData, resolveTemplate, resolveGroup } = useFormConfig();

// Wrap refData as computed for reactivity (DI pattern)
const refData = computed(() => {
  return { ...baseRefData } as Record<string, any>;
});

// EOT 조건부 표시: extensionOfTime 체크 여부에 따라 grantedDate, eotAttachment 필드 표시
const displaySections = computed(() => {
  const isEotChecked = !!formData['extensionOfTime.extensionOfTime'];

  return allSections.map(section => {
    if (section.id === 'extensionOfTime' && section.fields) {
      return {
        ...section,
        fields: section.fields.filter((f: any) => {
          if (f.visibleWhen) {
            return isEotChecked;
          }
          return true;
        }),
      };
    }
    return section;
  });
});

// Section refs
const sectionRefs: Record<string, any> = {};
function setSectionRef(id: string, el: any) {
  if (el) sectionRefs[id] = el;
}

// Panels
const openPanels = ref<string[]>(allSections.map(s => s.id));

// Form data - flat key map: "sectionId.fieldId" -> value
const formData: Record<string, any> = reactive({});

// Validation
const sectionErrors = ref<Record<string, string[]>>({});
const showAddressErrors = ref(false);

// Entity loading — 기존 task 흐름(props.entityCd 가 처음부터 주어짐)에서만 사용.
//   Standalone 흐름은 좌측 챗봇 entity-selector 위젯 → applyEntity() 로 직접 주입.
interface Entity {
  entityCd: string;
  companyName: string;
  regNo: string;
  regNoOld: string;
  clientCd: string;
}
const entities = ref<Entity[]>([]);

const loadEntities = async () => {
  try {
    const response = await apiClient.get('/startTask/common/entities');
    const data = (response as any)?.data || response;
    entities.value = data?.entities || [];
  } catch (error) {
    console.error('[ChangeAddressForm] Failed to load entities:', error);
    entities.value = [];
  }
};

// ── Field action 라우팅 (FieldRenderer append-inner 아이콘 클릭) ──
function handleFieldAction(payload: { sectionId: string; action: string; fieldId: string }) {
  if (payload.action === 'select-entity') {
    emit('entity-pick-request');
  } else if (payload.action === 'select-firm') {
    emit('firm-pick-request');
  }
}

/**
 * 챗봇에서 entity 가 선택되었을 때 부모가 호출.
 *   - companyNumber: regNoOld 우선 (SSM 등록번호), 없으면 regNo / registrationNo
 *   - companyName: companyName / entityName
 */
function applyEntity(entity: Record<string, any> | null | undefined) {
  if (!entity) return;
  const reg =
    entity.regNoOld ||
    entity.regNo ||
    entity.registrationNo ||
    entity.REG_NO ||
    entity.REG_NO_OLD ||
    '';
  const name =
    entity.companyName ||
    entity.entityName ||
    entity.COMPANY_NAME ||
    '';
  // 키가 reactive map 에 미존재 시에도 reactive trigger 되도록 Object.assign + 명시 키 보장.
  if (formData['entityInfo.companyNumber'] === undefined) formData['entityInfo.companyNumber'] = '';
  if (formData['entityInfo.companyName'] === undefined) formData['entityInfo.companyName'] = '';
  formData['entityInfo.companyNumber'] = reg;
  formData['entityInfo.companyName'] = name;
  console.log('[ChangeAddressForm] applyEntity', { reg, name, entity });
  emitFormData();
}

/**
 * 챗봇 officer-list 에서 선택된 officer 를 firmName 에 매핑.
 *   - firmName: contactName / name / firstName + surname 순으로 시도
 */
function applyOfficer(officer: Record<string, any> | null | undefined) {
  if (!officer) return;
  const name =
    officer.contactName ||
    officer.name ||
    [officer.firstName, officer.middleName, officer.surname].filter(Boolean).join(' ').trim() ||
    '';
  if (!name) return;
  if (formData['entityInfo.firmName'] === undefined) formData['entityInfo.firmName'] = '';
  formData['entityInfo.firmName'] = name;
  console.log('[ChangeAddressForm] applyOfficer', { name, officer });
  emitFormData();
}

// Initialize defaults from JSON config + entity auto-fill
onMounted(async () => {
  // Initialize ALL fields (ensure reactive keys exist for validation)
  for (const section of allSections) {
    if (section.fields) {
      for (const field of section.fields) {
        const key = `${section.id}.${field.id}`;
        if (formData[key] === undefined) {
          formData[key] = field.default !== undefined ? field.default : '';
        }
      }
    }
  }

  // Load entities and auto-fill from entityCd prop (Step 1에서 전달)
  if (props.entityCd) {
    await loadEntities();
    const entity = entities.value.find(e => e.entityCd === props.entityCd);
    if (entity) {
      formData['entityInfo.companyNumber'] = entity.regNoOld || entity.regNo || '';
      formData['entityInfo.companyName'] = entity.companyName || '';
    }
    emitFormData();
  }
});

// Handle field updates
function handleFieldUpdate(payload: { sectionId: string; fieldId: string; value: any }) {
  const key = `${payload.sectionId}.${payload.fieldId}`;
  formData[key] = payload.value;
  emitFormData();
}

// Handle address updates
function handleAddressUpdate(payload: { sectionId: string; addressId: string; value: Record<string, any> }) {
  const key = `${payload.sectionId}._addr.${payload.addressId}`;
  formData[key] = payload.value;
  emitFormData();
}

// Emit current form data
function emitFormData() {
  emit('update:formData', getFormData());
}

// Section icons
function getSectionIcon(section: SectionDef): string {
  const icons: Record<string, string> = {
    extensionOfTime: 'mdi-clock-plus-outline',
    entityInfo: 'mdi-domain',
    newAddress: 'mdi-map-marker',
    changeDetails: 'mdi-calendar-edit',
    declaration: 'mdi-check-decagram',
  };
  return icons[section.id] || 'mdi-circle-outline';
}

// ===== Exposed methods =====

/**
 * Validate the form
 */
async function validate(): Promise<boolean> {
  showAddressErrors.value = true;
  const errors: Record<string, string[]> = {};
  let hasError = false;
  const postcodes = (refData.value.postcodes as RefDataItem[]) || [];

  function isValidPostcode(value: string): boolean {
    if (!value) return false;
    if (postcodes.length === 0) return true;
    return postcodes.some((p: any) => p.value === value);
  }

  const visibleSections = displaySections.value;

  for (const section of visibleSections) {
    const msgs: string[] = [];

    // Validate required fields
    if (section.fields) {
      for (const field of section.fields) {
        if (field.required) {
          const key = `${section.id}.${field.id}`;
          const val = formData[key];
          if (!val && val !== 0 && val !== false) {
            msgs.push(`${tl(field.label || field.id)} ${tl('is required')}`);
          }
        }
      }
    }

    // Validate address blocks
    if (section.addressBlocks) {
      for (const ab of section.addressBlocks) {
        const prefix = ab.title ? `${tl(ab.title)} - ` : '';
        const addr = formData[`${section.id}._addr.${ab.id}`] || {};
        const addrIsMalaysia = (addr.country || 'MALAYSIA') === 'MALAYSIA';
        if (addrIsMalaysia) {
          if (!addr.state) msgs.push(`${prefix}${tl('State is required')}`);
          if (!addr.city) msgs.push(`${prefix}${tl('City is required')}`);
          if (!addr.postcode) msgs.push(`${prefix}${tl('Post Code is required')}`);
          else if (!isValidPostcode(addr.postcode)) msgs.push(`${prefix}${tl('Post Code is invalid')}`);
        }
        if (!addr.street01) msgs.push(`${prefix}${tl('Street is required')}`);
      }
    }

    // Declaration
    if (section.type === 'declaration' && section.required) {
      const val = formData[`${section.id}.declaration`] || formData['declaration'];
      if (!val) msgs.push(tl('Declaration must be checked'));
    }

    if (msgs.length > 0) {
      errors[section.id] = msgs;
      hasError = true;
    }
  }

  sectionErrors.value = errors;

  // Open panels with errors
  if (hasError) {
    const errorSections = Object.keys(errors);
    openPanels.value = [...new Set([...openPanels.value, ...errorSections])];
  }

  return !hasError;
}

/**
 * Get all form data as structured object
 */
function getFormData(): any {
  const result: Record<string, any> = {};

  for (const section of allSections) {
    if (section.type === 'standard' || section.type === 'declaration') {
      const sectionData: Record<string, any> = {};

      if (section.fields) {
        for (const field of section.fields) {
          const key = `${section.id}.${field.id}`;
          sectionData[field.id] = formData[key] ?? '';
        }
      }

      // Addresses
      if (section.addressBlocks) {
        sectionData._addresses = {};
        for (const ab of section.addressBlocks) {
          const key = `${section.id}._addr.${ab.id}`;
          sectionData._addresses[ab.id] = formData[key] || {};
        }
      }

      // Declaration
      if (section.type === 'declaration') {
        const declKey = `${section.id}.declaration`;
        sectionData.declaration = formData[declKey] || false;
      }

      result[section.id] = sectionData;
    }
  }

  return result;
}

/**
 * Reset the form
 */
function resetForm() {
  Object.keys(formData).forEach(key => delete formData[key]);
  sectionErrors.value = {};
  openPanels.value = allSections.map(s => s.id);
}

/**
 * Load saved data
 */
async function loadSavedData(data: any) {
  if (!data) return;

  for (const section of allSections) {
    const sectionData = data[section.id];
    if (!sectionData) continue;

    if (section.fields) {
      for (const field of section.fields) {
        if (sectionData[field.id] !== undefined) {
          formData[`${section.id}.${field.id}`] = sectionData[field.id];
        }
      }
    }

    // Addresses
    if (sectionData._addresses) {
      for (const [addrId, addrData] of Object.entries(sectionData._addresses)) {
        formData[`${section.id}._addr.${addrId}`] = addrData;
      }
    }

    // Declaration
    if (section.type === 'declaration' && sectionData.declaration !== undefined) {
      formData[`${section.id}.declaration`] = sectionData.declaration;
    }
  }

  // entityCd prop이 있으면 entity info 복원
  if (props.entityCd && entities.value.length === 0) {
    await loadEntities();
    const entity = entities.value.find(e => e.entityCd === props.entityCd);
    if (entity) {
      formData['entityInfo.companyNumber'] = entity.regNoOld || entity.regNo || '';
      formData['entityInfo.companyName'] = entity.companyName || '';
    }
  }
}

/**
 * Get validation errors as strings
 */
function getValidationErrors(): string[] {
  const errors: string[] = [];
  for (const [sectionId, msgs] of Object.entries(sectionErrors.value)) {
    const section = allSections.find(s => s.id === sectionId);
    const title = tl(section?.title || sectionId);
    errors.push(`${title}: ${msgs.length} ${tl('error(s)')}`);
    for (const msg of msgs) {
      errors.push(`  - ${msg}`);
    }
  }
  return errors;
}

defineExpose({
  validate,
  getFormData,
  resetForm,
  loadSavedData,
  getValidationErrors,
  applyEntity,
  applyOfficer,
});
</script>

<style scoped>
.ca-form {
  max-width: 100%;
}

:deep(.v-expansion-panel) {
  margin-bottom: 8px;
}

:deep(.v-expansion-panel-title) {
  min-height: 48px;
}

:deep(.v-expansion-panel-title--active) {
  min-height: 48px;
}
</style>
